import { Injectable } from '@angular/core';
import { generateClient } from 'aws-amplify/api';
import * as queries from '@graphql/queries';
import * as mutations from '@graphql/mutations';
import {
    CreatePrintedOrderInput,
    ModelSortDirection,
    PrintedOrder,
    UpdatePrintedOrderInput
} from '@app/core/services/api/API.service';
@Injectable({
    providedIn: 'root'
})
export class PrintedOrderService {
    private amplifyClient = generateClient();

    constructor() {}

    async getPrintedOrder(id: string): Promise<PrintedOrder | null> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.getPrintedOrder,
                    variables: { id }
                })
            ).data.getPrintedOrder;
            return data ? (data as PrintedOrder) : null;
        } catch (error) {
            console.error('Error finding PrintedOrder:', error);
            return null;
        }
    }

    async listPrintedOrders(): Promise<PrintedOrder[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.listPrintedOrders
                })
            ).data.listPrintedOrders.items;
            if (data) return data as PrintedOrder[];
        } catch (error) {
            console.error('Error finding PrintedOrder:', error);
            return null;
        }
    }
    async printedOrdersByBuyer(
        orderCreatorId: string
    ): Promise<PrintedOrder[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.PrintedOrdersByBuyer,
                    variables: {
                        orderCreatorId
                    }
                })
            ).data.PrintedOrdersByBuyer.items;
            if (data) return data as PrintedOrder[];
        } catch (error) {
            console.error('Error finding PrintedOrder:', error);
            return null;
        }
    }

    async printedOrdersByClient(
        printedOrderClientId: string
    ): Promise<PrintedOrder[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.PrintedOrdersByClient,
                    variables: {
                        printedOrderClientId
                    }
                })
            ).data.PrintedOrdersByClient.items;
            if (data) return data as PrintedOrder[];
        } catch (error) {
            console.error('Error finding PrintedOrder:', error);
            return null;
        }
    }

    async printedOrdersByOrderId(status: string): Promise<PrintedOrder[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.PrintedOrdersByOrderId,
                    variables: {
                        status
                    }
                })
            ).data.PrintedOrdersByOrderId.items;
            if (data) return data as PrintedOrder[];
        } catch (error) {
            console.error('Error finding PrintedOrder:', error);
            return null;
        }
    }

    async getLatestPrintedOrdersByOrderId(
        status: string,
        sortDirection: ModelSortDirection = ModelSortDirection.DESC,
        limit: number = 1
    ): Promise<{ items: PrintedOrder[]; nextToken?: string | null }> {
        try {
            return (
                await this.amplifyClient.graphql({
                    query: queries.PrintedOrdersByOrderId,
                    variables: { status, sortDirection, limit }
                })
            ).data.PrintedOrdersByOrderId;
        } catch (error) {
            console.error('Error finding PrintedOrder:', error);
            return { items: [] };
        }
    }

    async createPrintedOrder(
        input: CreatePrintedOrderInput
    ): Promise<PrintedOrder> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.createPrintedOrder,
                    variables: {
                        input
                    }
                })
            ).data.createPrintedOrder;
            if (data) return data as PrintedOrder;
        } catch (error) {
            console.error('Error creating PrintedOrder:', error);
            return null;
        }
    }
    async updatePrintedOrder(
        input: UpdatePrintedOrderInput
    ): Promise<PrintedOrder> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.updatePrintedOrder,
                    variables: {
                        input
                    }
                })
            ).data.updatePrintedOrder;
            if (data) return data as PrintedOrder;
        } catch (error) {
            console.error('Error updated PrintedOrder:', error);
            return null;
        }
    }

    async listAllPrintedOrdersByBuyer(
        orderCreatorId: string,
        sortDirection: ModelSortDirection = ModelSortDirection.DESC,
        pageLimit: number = 50
    ): Promise<PrintedOrder[]> {
        const orders: PrintedOrder[] = [];
        let nextToken: string | null = null;

        try {
            do {
                const response = (
                    await this.amplifyClient.graphql({
                        query: queries.PrintedOrdersByBuyer,
                        variables: {
                            orderCreatorId,
                            nextToken,
                            sortDirection,
                            limit: pageLimit
                        }
                    })
                ).data.PrintedOrdersByBuyer;

                if (response?.items) {
                    orders.push(...(response.items as PrintedOrder[]));
                }
                nextToken = response?.nextToken ?? null;
            } while (nextToken !== null);

            return orders;
        } catch (error) {
            console.error('Error finding PrintedOrder:', error);
            return [];
        }
    }

    async listAllPrintedOrdersByProvider(
        providerId: string,
        sortDirection: ModelSortDirection = ModelSortDirection.DESC,
        pageLimit: number = 50
    ): Promise<any[]> {
        const orders: any[] = [];
        let nextToken: string | null = null;

        try {
            do {
                const response = (
                    await this.amplifyClient.graphql({
                        query: queries.PrintedOrdersByProvider,
                        variables: {
                            provider_id: providerId,
                            nextToken,
                            sortDirection,
                            limit: pageLimit
                        }
                    })
                ).data.PrintedOrdersByProvider;

                if (response?.items) {
                    orders.push(...response.items);
                }
                nextToken = response?.nextToken ?? null;
            } while (nextToken !== null);

            return orders;
        } catch (error) {
            console.error('Error finding PrintedOrder:', error);
            return [];
        }
    }

    async listAllPrintedOrdersByOrderId(
        status: string,
        sortDirection: ModelSortDirection = ModelSortDirection.DESC,
        pageLimit: number = 50
    ): Promise<PrintedOrder[]> {
        const orders: PrintedOrder[] = [];
        let nextToken: string | null = null;

        try {
            do {
                const response = (
                    await this.amplifyClient.graphql({
                        query: queries.PrintedOrdersByOrderId,
                        variables: {
                            status,
                            nextToken,
                            sortDirection,
                            limit: pageLimit
                        }
                    })
                ).data.PrintedOrdersByOrderId;

                if (response?.items) {
                    orders.push(...(response.items as PrintedOrder[]));
                }
                nextToken = response?.nextToken ?? null;
            } while (nextToken !== null);

            return orders;
        } catch (error) {
            console.error('Error finding PrintedOrder:', error);
            return [];
        }
    }
}
